import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  findDwelling,
  findUnit,
  unitStatus,
  type Annotation,
  type Discipline,
  type DwellingStatus,
  type Manifest,
  type Unit,
} from '@alrimal/contracts';
import type { Hit, LoadProgress } from '@alrimal/engine';
import type { Viewer } from './viewer.ts';
import { useViewState } from './useViewState.ts';
import {
  fetchLiveUnits,
  fetchManifest,
  mergeLiveUnits,
  panoramaUrl,
  staticData,
} from './data.ts';
import { LocaleProvider, useLocale } from './LocaleContext.tsx';
import { directionOf, type Locale } from './i18n.ts';
import { TopBar } from './components/TopBar.tsx';
import { HeroVeil } from './components/HeroVeil.tsx';
import { Legend } from './components/Legend.tsx';
import { UnitTooltip } from './components/UnitTooltip.tsx';
import { LayerBar } from './components/LayerBar.tsx';
import { UnitPanel } from './components/UnitPanel.tsx';
import { LeadForm } from './components/LeadForm.tsx';
import { LoadingVeil } from './components/LoadingVeil.tsx';
import { AnnotationLayer } from './components/AnnotationLayer.tsx';
import { StatsHud } from './components/StatsHud.tsx';
import { Fallback } from './components/Fallback.tsx';

// Lazy on purpose: the 360° shell (and the engine path it pulls) downloads
// only for a buyer who actually opens a tour.
const PanoramaOverlay = lazy(() => import('./components/PanoramaOverlay.tsx'));

/**
 * Composition.
 *
 * Four screens — masterplan, unit, level, layer — and each one is a URL state
 * rather than a route (§7). There is no router: `useViewState` is the router,
 * `Viewer.applyState` is the renderer, and the back button works because the
 * history stack is the only place the state ever lived.
 */

export function App() {
  const [locale, setLocale] = useState<Locale>('ar');

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionOf(locale);
  }, [locale]);

  // The pre-rendered marketing shell has done its job — it was the first paint
  // and it is what a crawler reads. Remove it once React owns the page.
  useEffect(() => {
    document.getElementById('seo-shell')?.remove();
  }, []);

  return (
    <LocaleProvider locale={locale} setLocale={setLocale}>
      <Scene />
    </LocaleProvider>
  );
}

function Scene() {
  const { t } = useLocale();
  const { state, set, reset } = useViewState();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  const [units, setUnits] = useState<Unit[]>(staticData.units);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [progress, setProgress] = useState<LoadProgress | null>(null);
  const [ready, setReady] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [panoOpen, setPanoOpen] = useState(false);
  // The beauty layer greets masterplan landings only — a deep link into a
  // unit means the buyer was sent somewhere specific; take them there.
  const [heroOpen, setHeroOpen] = useState(() => state.unit === null);
  const [annotations, setAnnotations] = useState<readonly Annotation[]>([]);
  const [hoverUnit, setHoverUnit] = useState<string | null>(null);
  /** Last pointer position over the canvas, in viewport-local pixels. */
  const pointerRef = useRef({ x: 0, y: 0 });

  const reducedMotion = usePrefersReducedMotion();

  // -- Data ------------------------------------------------------------------

  useEffect(() => {
    const controller = new AbortController();
    void fetchManifest('/assets/manifest.json', controller.signal)
      .then(setManifest)
      .catch((error: unknown) => {
        // An abort is this effect being cleaned up, not a failure worth
        // telling a buyer about.
        if (controller.signal.aborted) return;
        setEngineError(error instanceof Error ? error.message : String(error));
      });
    return () => controller.abort();
  }, []);

  // Live status and price arrive after the page is already usable.
  useEffect(() => {
    const controller = new AbortController();
    void fetchLiveUnits(controller.signal).then((live) => {
      if (live) setUnits((current) => mergeLiveUnits(current, live));
    });
    return () => controller.abort();
  }, []);

  // The engine effect below deliberately does not re-run on catalogue changes,
  // so it reads the current units through a ref when it first constructs.
  const unitsRef = useRef(units);
  useEffect(() => {
    unitsRef.current = units;
  }, [units]);

  // Availability rings track the catalogue: bundled statuses immediately,
  // live /api/units rows the moment they merge.
  useEffect(() => {
    viewerRef.current?.setAvailability(availabilityOf(units));
  }, [units, ready]);

  // -- Engine ----------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || manifest === null) return;

    let cancelled = false;
    let teardown: (() => void) | null = null;

    // Three.js is the largest thing this page downloads and the buyer should
    // not be waiting on it to read the price or tap «اطلب السعر». Loading it
    // here rather than at module scope means the marketing shell is
    // interactive while the engine is still arriving.
    void import('./viewer.ts')
      .then(({ Viewer }) => {
        if (cancelled) return;

        let viewer: Viewer;
        try {
          viewer = new Viewer(canvas, {
            manifest,
            masterplan: staticData.masterplan,
            reducedMotion: prefersReducedMotionNow(),
          });
        } catch (error) {
          // Matched by name, not instanceof: importing the error class here
          // would put the whole engine — and three.js behind it — back on
          // the boot path this file exists to keep clean.
          if (error instanceof Error && error.name === 'WebGL2UnavailableError') {
            setUnsupported(true);
          } else {
            setEngineError(
              error instanceof Error ? error.message : String(error),
            );
          }
          return;
        }

        viewerRef.current = viewer;
        viewer.setAvailability(availabilityOf(unitsRef.current));
        const offProgress = viewer.streaming.onProgress(setProgress);
        const offHover = viewer.selection.onHover((hit) => {
          canvas.dataset['hovering'] = hit === null ? 'false' : 'true';
          setHoverUnit(hit?.unit ?? null);
        });
        const offSelect = viewer.selection.onSelect((hit: Hit | null) => {
          if (hit) set({ unit: hit.unit, level: null }, { replace: false });
        });
        const onPointerMove = (event: PointerEvent) => {
          const rect = canvas.getBoundingClientRect();
          pointerRef.current = {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          };
        };
        canvas.addEventListener('pointermove', onPointerMove, {
          passive: true,
        });
        const onResize = () => viewer.syncSize();
        window.addEventListener('resize', onResize);

        viewer.start();
        void viewer
          .loadInitial()
          .then(() => {
            if (!cancelled) setReady(true);
          })
          .catch((error: unknown) => {
            setEngineError(
              error instanceof Error ? error.message : String(error),
            );
          });

        teardown = () => {
          window.removeEventListener('resize', onResize);
          canvas.removeEventListener('pointermove', onPointerMove);
          offProgress();
          offHover();
          offSelect();
          viewer.dispose();
          viewerRef.current = null;
        };
      })
      .catch((error: unknown) => {
        setEngineError(error instanceof Error ? error.message : String(error));
      });

    return () => {
      cancelled = true;
      teardown?.();
      setReady(false);
    };
    // Built once, when the manifest lands. Rebuilding would mean a second
    // WebGL context on a canvas whose first one has been force-lost, which no
    // browser will give us — so every other input is applied to the live
    // instance instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, set]);

  // The motion preference changes the running viewer rather than replacing it.
  useEffect(() => {
    viewerRef.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion, ready]);

  // -- Chrome geometry → camera framing --------------------------------------

  // Measure before the camera moves: `applyState` frames the level, and it has
  // to know what the panel is covering when it does.
  useLayoutEffect(() => {
    const viewer = viewerRef.current;
    const canvas = canvasRef.current;
    if (!viewer || !canvas) return;
    viewer.setChromeRect(
      canvas.getBoundingClientRect(),
      panelRef.current?.getBoundingClientRect() ?? null,
    );
  }, [state.unit, leadOpen, ready]);

  // -- URL state → scene -----------------------------------------------------

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !ready) return;
    void viewer.applyState(state).catch((error: unknown) => {
      setEngineError(error instanceof Error ? error.message : String(error));
    });

    if (state.unit && state.level) {
      setAnnotations(viewer.annotationsFor(state.unit, state.level));
    } else {
      setAnnotations([]);
    }
  }, [state, ready]);

  // -- Derived ---------------------------------------------------------------

  const unit = state.unit ? findUnit(units, state.unit) : undefined;
  const dwelling = useMemo(() => {
    if (!unit) return null;
    if (state.dwelling) {
      return unit.dwellings.find((d) => d.id === state.dwelling) ?? null;
    }
    // A single-dwelling product needs no selector; a villa does, and gets one.
    return unit.dwellings.length === 1 ? (unit.dwellings[0] ?? null) : null;
  }, [unit, state.dwelling]);

  const selectedPair = useMemo(() => {
    if (!state.dwelling) return null;
    return findDwelling(units, state.dwelling) ?? null;
  }, [units, state.dwelling]);

  const onToggleLayer = useCallback(
    (discipline: Discipline) => {
      const next = state.layers.includes(discipline)
        ? state.layers.filter((item) => item !== discipline)
        : [...state.layers, discipline];
      set({ layers: next.length === 0 ? ['arch'] : next });
    },
    [set, state.layers],
  );

  const closePanel = useCallback(() => {
    setLeadOpen(false);
    setPanoOpen(false);
    reset();
  }, [reset]);

  // A tour belongs to the unit it was opened from; navigating away closes it.
  useEffect(() => {
    setPanoOpen(false);
  }, [state.unit]);

  // History can carry the buyer into a unit while the veil is up; the veil
  // never argues with navigation.
  useEffect(() => {
    if (state.unit !== null) setHeroOpen(false);
  }, [state.unit]);

  if (unsupported) {
    return (
      <Fallback
        project={staticData.project}
        units={units}
        warranties={staticData.warranties}
      />
    );
  }

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('nav.skip')}
      </a>

      <TopBar
        project={staticData.project}
        light={state.light}
        onLightChange={(light) => set({ light })}
        canGoBack={state.unit !== null}
        onBack={closePanel}
      />

      <main className="viewport" id="main">
        <canvas
          ref={canvasRef}
          className="viewport__canvas"
          aria-label={t('nav.viewport')}
          role="img"
        />
        <p className="visually-hidden">{t('nav.viewportHelp')}</p>

        {staticData.masterplan.provisional && state.unit === null && !heroOpen ? (
          <p className="notice notice--floating">
            <strong>{t('notice.provisional')}</strong>{' '}
            {staticData.masterplan.provisionalNoteAr}
          </p>
        ) : null}

        {viewerRef.current && annotations.length > 0 ? (
          <AnnotationLayer
            anchors={viewerRef.current.anchors}
            items={annotations}
          />
        ) : null}

        {hoverUnit !== null && state.unit === null ? (
          <UnitTooltip
            unit={findUnit(units, hoverUnit) ?? null}
            unitId={hoverUnit}
            pointer={pointerRef}
          />
        ) : null}

        {!heroOpen ? <Legend units={units} /> : null}

        {heroOpen ? (
          <HeroVeil
            projectName={staticData.project.nameAr}
            demoLabel={staticData.project.demoData ? t('hero.demoImage') : ''}
            onDismissed={() => setHeroOpen(false)}
          />
        ) : null}

        {leadOpen ? (
          <section className="panel" aria-label={t('form.title')} ref={panelRef}>
            <header className="panel__header">
              <div className="panel__heading">
                <h2 className="panel__title">{t('form.title')}</h2>
              </div>
              <button
                type="button"
                className="panel__close"
                onClick={() => setLeadOpen(false)}
                aria-label={t('action.close')}
              >
                ✕
              </button>
            </header>
            <div className="panel__body">
              <LeadForm
                units={units}
                selected={selectedPair}
                contactPhone={staticData.project.contact.phone}
                whatsappPhone={staticData.project.contact.whatsapp ?? null}
                source={state.dwelling ? `unit:${state.dwelling}` : 'masterplan'}
              />
            </div>
          </section>
        ) : unit ? (
          <UnitPanel
            unit={unit}
            dwelling={dwelling}
            levelCode={state.level}
            warranties={staticData.warranties}
            onSelectDwelling={(dwellingId) => set({ dwelling: dwellingId })}
            onSelectLevel={(level) => set({ level })}
            onRequestPrice={() => setLeadOpen(true)}
            {...(unit.panoramaSet !== null
              ? { onOpenPanorama: () => setPanoOpen(true) }
              : {})}
            onClose={closePanel}
            containerRef={panelRef}
          />
        ) : null}

        <LoadingVeil progress={progress} />

        {engineError ? (
          <p className="notice notice--floating" role="alert">
            {engineError}
          </p>
        ) : null}

        {import.meta.env.DEV && viewerRef.current ? (
          <StatsHud
            core={viewerRef.current.core}
            masterplan={state.unit === null}
          />
        ) : null}
      </main>

      <LayerBar
        active={state.layers}
        warranties={staticData.warranties}
        onToggle={onToggleLayer}
      />

      {state.unit === null && !leadOpen && !heroOpen ? (
        <button
          type="button"
          className="btn btn--primary cta-floating"
          onClick={() => setLeadOpen(true)}
        >
          {t('action.requestPrice')}
        </button>
      ) : null}

      {/* Outside <main>: the viewport's backdrop-filter would otherwise become
          the containing block for this fixed overlay and letterbox it. */}
      {panoOpen && unit && unit.panoramaSet !== null ? (
        <Suspense fallback={null}>
          <PanoramaOverlay
            title={`${dwelling?.labelAr ?? unit.dwellings[0]?.labelAr ?? unit.id} — ${t('action.panorama')}`}
            src={panoramaUrl(unit.panoramaSet)}
            {...(staticData.project.demoData
              ? { demoNote: t('pano.demo') }
              : {})}
            onClose={() => setPanoOpen(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

/** Unit-level status rollup, keyed for the availability rings. */
function availabilityOf(
  units: readonly Unit[],
): Map<string, DwellingStatus> {
  return new Map(units.map((unit) => [unit.id, unitStatus(unit)]));
}

function prefersReducedMotionNow(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotionNow);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
